/**
 * Lightweight in-memory resource cache with TTL + in-flight dedupe.
 *
 * Used so dashboard navigations remounting page hooks can reuse warm data
 * instead of flashing loaders and re-hitting the API every time.
 */

export interface ResourceCacheOptions {
  /** Time-to-live in milliseconds. Default: 5 minutes. */
  ttlMs?: number;
}

export interface ResourceCache<T> {
  /** Synchronously read a still-fresh value, or null. */
  peek: () => T | null;
  /** Fetch via `fetcher`, sharing in-flight work and caching the result. */
  get: (
    fetcher: () => Promise<T>,
    options?: { force?: boolean },
  ) => Promise<T>;
  /** Seed / overwrite the cache (e.g. after a mutation response). */
  set: (data: T) => void;
  /** Drop cached value and any in-flight promise. */
  invalidate: () => void;
  /** Whether a non-expired value is present. */
  isFresh: () => boolean;
}

export interface KeyedResourceCache<T> {
  peek: (key: string) => T | null;
  get: (
    key: string,
    fetcher: () => Promise<T>,
    options?: { force?: boolean },
  ) => Promise<T>;
  set: (key: string, data: T) => void;
  invalidate: (key: string) => void;
  invalidateAll: () => void;
  isFresh: (key: string) => boolean;
}

const DEFAULT_TTL_MS = 5 * 60 * 1000;

export function createResourceCache<T>(
  options: ResourceCacheOptions = {},
): ResourceCache<T> {
  const ttlMs = options.ttlMs ?? DEFAULT_TTL_MS;
  let cached: { data: T; at: number } | null = null;
  let inflight: Promise<T> | null = null;

  const isFresh = (): boolean =>
    !!cached && Date.now() - cached.at < ttlMs;

  const peek = (): T | null => (isFresh() ? cached!.data : null);

  const set = (data: T): void => {
    cached = { data, at: Date.now() };
  };

  const invalidate = (): void => {
    cached = null;
    inflight = null;
  };

  const get = async (
    fetcher: () => Promise<T>,
    opts: { force?: boolean } = {},
  ): Promise<T> => {
    if (!opts.force) {
      if (isFresh()) {
        return cached!.data;
      }
      if (inflight) {
        return inflight;
      }
    }

    inflight = fetcher()
      .then((data) => {
        set(data);
        return data;
      })
      .finally(() => {
        inflight = null;
      });

    return inflight;
  };

  return { peek, get, set, invalidate, isFresh };
}

export function createKeyedResourceCache<T>(
  options: ResourceCacheOptions = {},
): KeyedResourceCache<T> {
  const ttlMs = options.ttlMs ?? DEFAULT_TTL_MS;
  const cached = new Map<string, { data: T; at: number }>();
  const inflight = new Map<string, Promise<T>>();

  const isFresh = (key: string): boolean => {
    const entry = cached.get(key);
    return !!entry && Date.now() - entry.at < ttlMs;
  };

  const peek = (key: string): T | null =>
    isFresh(key) ? cached.get(key)!.data : null;

  const set = (key: string, data: T): void => {
    cached.set(key, { data, at: Date.now() });
  };

  const invalidate = (key: string): void => {
    cached.delete(key);
    inflight.delete(key);
  };

  const invalidateAll = (): void => {
    cached.clear();
    inflight.clear();
  };

  const get = async (
    key: string,
    fetcher: () => Promise<T>,
    opts: { force?: boolean } = {},
  ): Promise<T> => {
    if (!opts.force) {
      if (isFresh(key)) {
        return cached.get(key)!.data;
      }
      const pending = inflight.get(key);
      if (pending) {
        return pending;
      }
    }

    const promise = fetcher()
      .then((data) => {
        set(key, data);
        return data;
      })
      .finally(() => {
        inflight.delete(key);
      });

    inflight.set(key, promise);
    return promise;
  };

  return { peek, get, set, invalidate, invalidateAll, isFresh };
}
